import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Notice } from '../components/ui';
import { AuthStage, GlassField, GlassLink, GlassPanel } from '../components/layout/AuthStage';
import { api } from '../lib/api';
import { useAuth } from '../store/auth';

/**
 * Die Masken rund um die E-Mail-Adresse: bestätigen, Passwort anfordern,
 * Passwort setzen.
 *
 * Alle drei liegen in einer Datei, weil sie denselben Ablauf teilen – Token aus
 * der Adresszeile, ein Aufruf, danach angemeldet – und getrennt nur durch ihre
 * Texte sind.
 */

/** `/bestaetigen?token=…` – der Link aus der Registrierungsmail. */
export function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [state, setState] = useState<'laeuft' | 'fehler'>('laeuft');
  const [message, setMessage] = useState('');

  /*
   * Der Aufruf löst das Token ein und ist damit nicht wiederholbar. Im
   * Entwicklungsmodus lässt React einen Effekt zweimal laufen; ohne diese
   * Sperre entwertete der erste Durchlauf das Token und der zweite meldete es
   * als bereits verwendet.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    if (!token) {
      setState('fehler');
      setMessage('In der Adresse fehlt das Token. Öffne den Link aus der E-Mail noch einmal.');
      return;
    }

    api
      .verifyEmail(token)
      .then((result) => {
        adopt(result);
        navigate('/', { replace: true });
      })
      .catch((error: Error) => {
        setState('fehler');
        setMessage(error.message);
      });
  }, [token, adopt, navigate]);

  return (
    <AuthStage>
      <GlassPanel title="Bestätigen">
        {state === 'laeuft' ? (
          <p className="text-sm text-panel-700 dark:text-white/70">Deine E-Mail-Adresse wird bestätigt …</p>
        ) : (
          <>
            <Notice tone="danger">{message}</Notice>
            <p className="text-sm text-panel-700 dark:text-white/70">
              Bestätigungslinks gelten 24 Stunden und lassen sich nur einmal verwenden. Fordere unten einen
              neuen an.
            </p>
            <ResendForm />
          </>
        )}
      </GlassPanel>
    </AuthStage>
  );
}

/** Neue Bestätigungsmail anfordern – auch eigenständig unter `/bestaetigen`. */
export function ResendForm({ initialEmail = '' }: { initialEmail?: string }) {
  const [email, setEmail] = useState(initialEmail);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.resendVerification(email);
    } catch {
      /* Auch ein Fehler wird nicht angezeigt – siehe Hinweis unten. */
    }
    setBusy(false);
    setDone(true);
  };

  if (done) {
    return (
      <Notice tone="info">
        Falls für {email} ein unbestätigtes Konto besteht, ist die E-Mail unterwegs.
      </Notice>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <GlassField label="E-Mail" type="email" value={email} onChange={setEmail} required autoComplete="email" />
      <Button type="submit" variant="primary" loading={busy} className="w-full">
        Bestätigungsmail erneut senden
      </Button>
    </form>
  );
}

/** `/passwort-vergessen` – Anforderung der Mail zum Zurücksetzen. */
export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      await api.forgotPassword(email);
    } catch {
      /* Bewusst geschluckt: siehe die Meldung unten. */
    }
    setBusy(false);
    setDone(true);
  };

  return (
    <AuthStage>
      <GlassPanel
        title="Passwort"
        onSubmit={done ? undefined : submit}
        footer={
          <div className="border-t border-black/10 bg-black/[0.04] px-5 py-2.5 text-center dark:border-white/10 dark:bg-black/25">
            <GlassLink to="/login">Zurück zur Anmeldung</GlassLink>
          </div>
        }
      >
        {done ? (
          /*
           * Immer dieselbe Antwort, unabhängig davon, ob es das Konto gibt.
           * Andernfalls wäre diese Maske ein bequemes Verzeichnis: Wer wissen
           * will, ob jemand hier ein Konto hat, gibt dessen Adresse ein.
           */
          <Notice tone="info">
            Falls für {email} ein Konto besteht, ist die E-Mail unterwegs. Der Link darin gilt eine Stunde.
          </Notice>
        ) : (
          <>
            <p className="text-sm text-panel-700 dark:text-white/70">
              Gib deine Adresse ein. Wir schicken dir einen Link, mit dem du ein neues Passwort setzen kannst.
            </p>
            <GlassField
              label="E-Mail"
              type="email"
              value={email}
              onChange={setEmail}
              required
              autoComplete="email"
              placeholder="admin@example.de"
            />
            <Button type="submit" variant="primary" loading={busy} className="w-full">
              Link anfordern
            </Button>
          </>
        )}
      </GlassPanel>
    </AuthStage>
  );
}

/** `/passwort-neu?token=…` – neues Passwort setzen. */
export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const { adopt } = useAuth();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (password !== repeat) {
      setError('Die beiden Passwörter stimmen nicht überein.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      adopt(await api.resetPassword(token, password));
      navigate('/', { replace: true });
    } catch (err) {
      setError((err as Error).message);
      setBusy(false);
    }
  };

  return (
    <AuthStage>
      <GlassPanel
        title="Neues Passwort"
        onSubmit={submit}
        footer={
          <div className="border-t border-black/10 bg-black/[0.04] px-5 py-2.5 text-center dark:border-white/10 dark:bg-black/25">
            <GlassLink to="/passwort-vergessen">Neuen Link anfordern</GlassLink>
          </div>
        }
      >
        {!token && <Notice tone="danger">In der Adresse fehlt das Token. Öffne den Link aus der E-Mail noch einmal.</Notice>}
        <GlassField
          label="Neues Passwort"
          type="password"
          value={password}
          onChange={setPassword}
          required
          autoComplete="new-password"
          hint="Mindestens 10 Zeichen."
        />
        <GlassField
          label="Wiederholen"
          type="password"
          value={repeat}
          onChange={setRepeat}
          required
          autoComplete="new-password"
        />
        {error && <Notice tone="danger">{error}</Notice>}
        <p className="text-xs text-panel-600 dark:text-white/45">
          Mit dem neuen Passwort werden alle offenen Sitzungen abgemeldet – auch auf anderen Geräten.
        </p>
        <Button type="submit" variant="primary" loading={busy} disabled={!token} className="w-full">
          Passwort setzen und anmelden
        </Button>
      </GlassPanel>
    </AuthStage>
  );
}
